// Bureau — l'espace de l'office manager. Une page, cinq vues :
//   Journée   les moments du jour et leurs points de contrôle ;
//   Suivis    factures, fournisseurs, événements, achats, studio, tâches ;
//   Stock     trois niveaux par article, liste d'achats à faire valider ;
//   Pilotage  (bureau.manage) validations en attente, historique, confier ;
//   Réglages  (bureau.manage) personnes, moments, factures, articles.
// La vue courante vit dans ?vue= pour que les notifications y mènent.
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { useMe } from '../api/hooks.js';
import {
  useBureauAccess, useBureauConfig, useBureauFollows, useBureauMe, useBureauOverview, useBureauStock, useBureauToday,
} from '../api/bureau.js';
import { todayYmd } from '../lib/date.js';
import { JourneeView } from '../components/bureau/JourneeView.js';
import { SuivisView } from '../components/bureau/SuivisView.js';
import { StockView } from '../components/bureau/StockView.js';
import { PilotageView } from '../components/bureau/PilotageView.js';
import { ReglagesView } from '../components/bureau/ReglagesView.js';
import '../styles/bureau.css';

type Vue = 'journee' | 'suivis' | 'stock' | 'pilotage' | 'reglages';

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

export function BureauPage() {
  const me = useMe();
  const access = useBureauAccess();
  const bme = useBureauMe();
  const [params, setParams] = useSearchParams();
  const date = todayYmd();
  const today = useBureauToday(date);
  const follows = useBureauFollows('open');
  const stock = useBureauStock();
  const overview = useBureauOverview(access.manage);
  const config = useBureauConfig(access.manage && params.get('vue') === 'reglages');

  const defaultVue: Vue = access.manage && bme.data && !bme.data.isAssignee ? 'pilotage' : 'journee';
  const asked = params.get('vue') as Vue | null;
  const vue: Vue = asked && ['journee', 'suivis', 'stock', 'pilotage', 'reglages'].includes(asked)
    && (access.manage || (asked !== 'pilotage' && asked !== 'reglages')) ? asked : defaultVue;
  const go = (v: Vue) => setParams((p) => { const n = new URLSearchParams(p); n.set('vue', v); return n; }, { replace: true });

  const moments = today.data?.moments ?? [];
  const items = moments.flatMap((m) => m.items);
  const doneItems = items.filter((i) => i.done).length;
  const lateMoments = moments.filter((m) => m.status === 'late').length;
  const nowMoment = moments.find((m) => m.status === 'now');
  const open = follows.data?.items ?? [];
  const lateFollows = open.filter((f) => f.level === 'late').length;
  const urgentFollows = open.filter((f) => f.level === 'warn').length;
  const waiting = open.filter((f) => f.waitingOn).length;
  const toBuy = (stock.data?.items ?? []).filter((i) => i.level !== 'OK').length;
  const inbox = overview.data?.inbox.length ?? 0;
  const nextBill = open
    .filter((f) => f.type === 'FACTURE' && f.dueAt)
    .sort((a, b) => a.dueAt!.localeCompare(b.dueAt!))[0];

  const firstName = me.data?.user.name.split(' ')[0] ?? '';
  const mood = useMemo(() => {
    if (vue === 'pilotage' || vue === 'reglages') {
      if (inbox > 0) return inbox === 1 ? 'une validation t’attend.' : `${inbox} validations t’attendent.`;
      return 'rien n’attend de validation.';
    }
    if (lateMoments + lateFollows > 0) return `${lateMoments + lateFollows} chose${lateMoments + lateFollows > 1 ? 's' : ''} en retard.`;
    if (nowMoment) return `c’est l’heure : ${nowMoment.label.toLowerCase()}.`;
    if (urgentFollows > 0) return `${urgentFollows} suivi${urgentFollows > 1 ? 's' : ''} à surveiller.`;
    return 'tout est à jour pour l’instant.';
  }, [vue, inbox, lateMoments, lateFollows, nowMoment, urgentFollows]);

  const tabs: Array<[Vue, string, number]> = [
    ['journee', 'Journée', lateMoments],
    ['suivis', 'Suivis', lateFollows],
    ['stock', 'Stock', toBuy],
    ...(access.manage ? ([['pilotage', 'Pilotage', inbox], ['reglages', 'Réglages', 0]] as Array<[Vue, string, number]>) : []),
  ];

  return (
    <div className="bxapp" data-testid="bureau-page">
      <div className="page-header">
        <div className="page-eyebrow">{DATE_FMT.format(new Date())}{today.data ? ` — ${today.data.now}` : ''}</div>
        <h1 className="page-title">Bonjour {firstName}, <em>{mood}</em></h1>
        <p className="page-subtitle">La journée de l’office manager, ses suivis et le stock du bureau.</p>
      </div>

      <div className="kpis bx-kpis">
        <div className="kpi" data-testid="bx-kpi-journee">
          <div className="kpi-label"><span className="kpi-label-dot" style={{ background: 'var(--ok)' }} />Journée</div>
          <div className="kpi-value">{doneItems}<span className="kpi-suffix">/{items.length}</span></div>
          <div className="kpi-foot"><span>{lateMoments ? `${lateMoments} moment${lateMoments > 1 ? 's' : ''} en retard` : 'points de contrôle faits'}</span></div>
        </div>
        <div className="kpi kpi-warn">
          <div className="kpi-label"><span className="kpi-label-dot" />Factures</div>
          <div className="kpi-value">{nextBill ? nextBill.dueLabel.match(/J-\d+/)?.[0] ?? '!' : '—'}</div>
          <div className="kpi-foot"><span>{nextBill ? nextBill.title : 'aucune échéance ouverte'}</span></div>
        </div>
        <div className="kpi kpi-info">
          <div className="kpi-label"><span className="kpi-label-dot" />En attente</div>
          <div className="kpi-value">{waiting}</div>
          <div className="kpi-foot"><span>validation ou réponse d’un tiers</span></div>
        </div>
        <div className="kpi kpi-bad">
          <div className="kpi-label"><span className="kpi-label-dot" />Suivis en retard</div>
          <div className="kpi-value">{lateFollows}</div>
          <div className="kpi-foot"><span>{open.length} suivi{open.length > 1 ? 's' : ''} ouvert{open.length > 1 ? 's' : ''}</span></div>
        </div>
      </div>

      <div className="toolbar bx-toolbar">
        <div className="segmented bx-tabs" role="tablist" aria-label="Vue du Bureau">
          {tabs.map(([id, label, n]) => (
            <button
              type="button" key={id} role="tab" aria-selected={vue === id}
              className={vue === id ? 'seg active' : 'seg'} onClick={() => go(id)} data-testid={`bx-tab-${id}`}
            >
              <span className="seg-dot" />{label}
              {n > 0 && <span className={id === 'stock' ? 'bx-badge warn' : 'bx-badge'}>{n}</span>}
            </button>
          ))}
        </div>
      </div>

      {vue === 'journee' && (today.data ? <JourneeView today={today.data} /> : <div className="card bx-empty">Chargement de la journée…</div>)}
      {vue === 'suivis' && <SuivisView canManage={access.manage} />}
      {vue === 'stock' && <StockView canManage={access.manage} />}
      {vue === 'pilotage' && (overview.data
        ? <PilotageView overview={overview.data} onSettings={() => go('reglages')} />
        : <div className="card bx-empty">Chargement du pilotage…</div>)}
      {vue === 'reglages' && (config.data ? <ReglagesView config={config.data} /> : <div className="card bx-empty">Chargement des réglages…</div>)}
    </div>
  );
}
